import { Calendar, Check, ChevronDown, CircleDot, FileText, Hash, Image as ImageIcon, ListChecks, Paperclip, Send, Sparkles, Type, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import { uploadAccept } from '../utils/fieldTypes';
import { getFormRendererTexts } from '../utils/formRendererTexts';
import { useLang } from '../LangContext';

// Alan tipine göre küçük ikon eşlemesi
const FIELD_ICON = {
  text: Type,
  textarea: FileText,
  number: Hash,
  date: Calendar,
  select: ChevronDown,
  radio: CircleDot,
  multiselect: ListChecks,
  imageChoiceSingle: ImageIcon,
  imageChoiceMulti: ImageIcon,
  checkbox: Check,
  file: Paperclip,
  photo: ImageIcon,
  video: Video,
};

// Görsel seçim seçeneklerini normalize eder — string/obje karışık gelebilir
function normalizeImageOption(option) {
  if (option && typeof option === 'object') {
    return { label: option.label || '', image: option.image || '' };
  }
  if (typeof option === 'string') {
    try {
      const parsed = JSON.parse(option);
      if (parsed && typeof parsed === 'object') {
        return { label: parsed.label || '', image: parsed.image || '' };
      }
    } catch (error) {
      // Düz metin etiketi olarak kullan
    }
    return { label: option, image: '' };
  }
  return { label: '', image: '' };
}

function getFieldIcon(type) {
  const Icon = FIELD_ICON[type] || Type;
  return <Icon size={14} />;
}

export default function DynamicFormRenderer({ form, onSubmit }) {
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  // Çift gönderimi (double submit) engellemek için: gönderim devam ederken buton disable edilir
  const [submitting, setSubmitting] = useState(false);
  const { lang } = useLang();
  // Statik arayüz metinleri formun kendi çözümlenen diline göre; yoksa (ör. admin önizlemesi)
  // panelin genel dil değiştiricisine düşer
  const texts = useMemo(() => getFormRendererTexts(form.language || lang), [form.language, lang]);

  const sections = useMemo(() => (
    form.sections?.length > 0
      ? form.sections
      : [{ id: 'default', title: '', description: '', fields: form.fields || [] }]
  ), [form]);

  function changeValue(fieldId, value) {
    setValues((current) => ({ ...current, [fieldId]: value }));
  }

  function changeFile(fieldId, file) {
    setFiles((current) => ({ ...current, [fieldId]: file }));
  }

  async function submit(event) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      const allFields = sections.flatMap((section) => section.fields || []);
      allFields.forEach((field) => {
        if (field.fieldType === 'file' || field.fieldType === 'photo' || field.fieldType === 'video') {
          if (files[field.id]) {
            data.append(field.id, files[field.id]);
          }
        } else {
          data.append(field.id, values[field.id] ?? '');
        }
      });
      const submitted = await onSubmit(form.id, data);
      if (submitted === false) {
        return;
      }

      setValues({});
      setFiles({});
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="df-form" onSubmit={submit}>
      {/* Form kapağı */}
      <header className="df-hero">
        <div className="df-hero-content">
          <span className="df-hero-eyebrow"><Sparkles size={13} /> {texts.formEyebrow}</span>
          <h2>{form.name}</h2>
          {form.description && <p>{form.description}</p>}
        </div>
      </header>

      {/* Bölümler */}
      <div className="df-sections">
        {sections.map((section, sectionIndex) => (
          <section className="df-section" key={section.id}>
            {(section.title || section.description) && (
              <div className="df-section-heading">
                <span className="df-section-index">{String(sectionIndex + 1).padStart(2, '0')}</span>
                <div>
                  {section.title && <h3>{section.title}</h3>}
                  {section.description && <p>{section.description}</p>}
                </div>
              </div>
            )}
            <div className="df-section-grid">
              {section.fields.map((field) => {
                const layoutClass = field.width === 'half'
                  ? (field.column === 'right' ? 'df-field-half-right' : 'df-field-half-left')
                  : 'df-field-full';
                return (
                  <FieldRow
                    key={field.id}
                    field={field}
                    className={layoutClass}
                    value={values[field.id] ?? ''}
                    file={files[field.id]}
                    onValue={changeValue}
                    onFile={changeFile}
                    texts={texts}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Gönder */}
      <div className="df-footer">
        <button className="btn btn-primary df-submit" type="submit" disabled={submitting} aria-busy={submitting}>
          <Send size={16} /> {submitting ? texts.submitting : texts.submit}
        </button>
      </div>
    </form>
  );
}

function FieldRow({ field, className, value, file, onValue, onFile, texts }) {
  const isCheckbox = field.fieldType === 'checkbox';
  // "text" alanlarında etiket ikonu gösterilmez — yalın etiket yeterli
  const showIcon = field.fieldType !== 'text';
  return (
    <div className={`df-field ${className} ${isCheckbox ? 'df-field-checkbox' : ''}`}>
      {!isCheckbox && (
        <label className="df-label">
          {showIcon && <span className="df-label-icon">{getFieldIcon(field.fieldType)}</span>}
          <span className="df-label-text">{field.label}</span>
          {field.isRequired && <span className="df-required" title={texts.requiredTitle}>*</span>}
        </label>
      )}
      <FieldInput field={field} value={value} file={file} onValue={onValue} onFile={onFile} texts={texts} />
    </div>
  );
}

function FieldInput({ field, value, file, onValue, onFile, texts }) {
  if (field.fieldType === 'textarea') {
    return (
      <textarea
        className="df-input df-textarea"
        required={field.isRequired}
        placeholder={field.placeholder || ''}
        value={value}
        onChange={(event) => onValue(field.id, event.target.value)}
      />
    );
  }

  if (field.fieldType === 'select') {
    return (
      <div className="df-select-wrap">
        <select
          className="df-input df-select"
          required={field.isRequired}
          value={value}
          onChange={(event) => onValue(field.id, event.target.value)}
        >
          <option value="">{texts.selectPlaceholder}</option>
          {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
        <ChevronDown size={16} className="df-select-icon" />
      </div>
    );
  }

  if (field.fieldType === 'radio' || field.fieldType === 'multiselect') {
    const isMulti = field.fieldType === 'multiselect';
    const options = field.options || [];
    // Çoklu seçimde değer "|" ayracı ile ayrılır, tekli seçimde tek bir string tutulur
    const selected = isMulti
      ? String(value || '').split('|').filter(Boolean)
      : (value ? [String(value)] : []);

    const toggle = (option) => {
      if (isMulti) {
        // Çoklu — tıklanan aktifse kaldır, değilse ekle
        const next = selected.includes(option)
          ? selected.filter((item) => item !== option)
          : [...selected, option];
        onValue(field.id, next.join('|'));
      } else {
        // Tekli — aynı seçiliyse temizle, değilse seç
        onValue(field.id, selected[0] === option ? '' : option);
      }
    };

    return (
      <div className={`df-choice df-choice-${isMulti ? 'multi' : 'single'}`}>
        {options.length === 0 && <p className="muted">{texts.noOptions}</p>}
        <div className="df-choice-list">
          {options.map((option, optionIndex) => {
            const isActive = selected.includes(option);
            return (
              <button
                type="button"
                key={`${option}-${optionIndex}`}
                className={`df-choice-item ${isActive ? 'is-active' : ''}`}
                onClick={() => toggle(option)}
              >
                <span className={`df-choice-indicator ${isMulti ? 'is-square' : 'is-circle'}`} aria-hidden="true">
                  {isActive && (isMulti ? <Check size={12} /> : <span className="df-choice-dot" />)}
                </span>
                <span className="df-choice-text">{option}</span>
              </button>
            );
          })}
        </div>
        {/* required doğrulaması için gizli giriş */}
        {field.isRequired && (
          <input
            tabIndex={-1}
            aria-hidden="true"
            className="df-choice-required"
            value={selected.join('|')}
            required
            onChange={() => {}}
          />
        )}
      </div>
    );
  }

  if (field.fieldType === 'imageChoiceSingle' || field.fieldType === 'imageChoiceMulti') {
    const isMulti = field.fieldType === 'imageChoiceMulti';
    const options = (field.options || []).map(normalizeImageOption);
    // Tekli seçimde tek string, çokluda | ayraçlı string
    const selected = isMulti
      ? String(value || '').split('|').filter(Boolean)
      : (value ? [String(value)] : []);

    const toggle = (label) => {
      if (isMulti) {
        // Çoklu — tıklanan aktifse kaldır, değilse ekle
        const next = selected.includes(label)
          ? selected.filter((item) => item !== label)
          : [...selected, label];
        onValue(field.id, next.join('|'));
      } else {
        // Tekli — aynı seçiliyse temizle, değilse seç
        onValue(field.id, selected[0] === label ? '' : label);
      }
    };

    return (
      <div className="df-image-choice">
        {/* Görsel seçenekler */}
        {options.length === 0 && <p className="muted">{texts.noOptions}</p>}
        <div className="df-image-choice-grid">
          {options.map((option, optionIndex) => {
            const isActive = selected.includes(option.label);
            return (
              <button
                type="button"
                key={`${option.label}-${optionIndex}`}
                className={`df-image-choice-card ${isActive ? 'is-active' : ''} ${isMulti ? '' : 'is-single'}`}
                onClick={() => toggle(option.label)}
              >
                <span className="df-image-choice-image">
                  {option.image ? (
                    <img src={option.image} alt={option.label} />
                  ) : (
                    <ImageIcon size={28} />
                  )}
                </span>
                <span className="df-image-choice-label">{option.label || texts.optionFallback(optionIndex + 1)}</span>
                <span className="df-image-choice-check" aria-hidden="true">
                  {isActive && <Check size={14} />}
                </span>
              </button>
            );
          })}
        </div>
        {/* required doğrulaması için gizli giriş */}
        {field.isRequired && (
          <input
            tabIndex={-1}
            aria-hidden="true"
            className="df-image-choice-required"
            value={selected.join('|')}
            required
            onChange={() => {}}
          />
        )}
      </div>
    );
  }

  if (field.fieldType === 'checkbox') {
    return (
      <label className="df-checkbox">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(event) => onValue(field.id, event.target.checked ? 'true' : 'false')}
        />
        <span className="df-checkbox-box"><Check size={14} /></span>
        <span className="df-checkbox-text">
          {field.label}{field.isRequired ? ' *' : ''}
        </span>
      </label>
    );
  }

  if (field.fieldType === 'file' || field.fieldType === 'photo' || field.fieldType === 'video') {
    const Icon = FIELD_ICON[field.fieldType] || Paperclip;
    return (
      <label className={`df-upload ${file ? 'has-file' : ''}`}>
        <input
          type="file"
          required={field.isRequired}
          accept={uploadAccept(field)}
          onChange={(event) => onFile(field.id, event.target.files?.[0])}
        />
        <span className="df-upload-icon"><Icon size={18} /></span>
        <span className="df-upload-text">
          <strong>{file ? file.name : (field.placeholder || texts.uploadPlaceholder)}</strong>
          <small>
            {field.fieldType === 'photo' && 'JPG, PNG, WEBP'}
            {field.fieldType === 'video' && 'MP4, MOV, WEBM'}
            {field.fieldType === 'file' && (file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : texts.fileHint)}
          </small>
        </span>
      </label>
    );
  }

  return (
    <input
      className="df-input"
      required={field.isRequired}
      type={field.fieldType === 'number' || field.fieldType === 'date' ? field.fieldType : 'text'}
      placeholder={field.placeholder || ''}
      value={value}
      onChange={(event) => onValue(field.id, event.target.value)}
    />
  );
}